'use client';

import { useEffect } from 'react';
import { useConnection } from './store/connection';
import { getActiveSocket } from './useSocket';

/**
 * Tells the BE which chat (session or link) the user is currently
 * viewing. Used by send-side flows to fold receiver-side `lastReadAt`
 * into the same UPDATE that bumps `<receiver>LastMessageAt`, so messages
 * arriving on devices the user is actively watching produce no
 * transient unread dot before the FE round-trips a /read.
 *
 * Visibility-aware on purpose: if the chat tab is visible we're
 * "present"; if the tab is hidden (other tab in foreground, window
 * minimised, screen locked, mobile app backgrounded) we're "absent",
 * so a message arriving in that window leaves the unread dot intact
 * for when the user comes back.
 *
 * Triggers:
 *   - Mount + socket connected + visible → emit `presence:set { kind, id }`.
 *   - visibilitychange → re-emit set/clear depending on the new state.
 *   - Unmount (route change away) → emit `presence:clear` if the socket
 *     is still up.
 *   - Socket disconnect (network drop, tab close) → BE auto-clears via
 *     the gateway disconnect handler; no FE work.
 *   - Socket reconnect → `socketId` dep change re-runs the effect and
 *     re-emits `set` (or `clear`) based on current visibility.
 */
export function useChatPresence(kind: 'session' | 'link', id: string | undefined) {
  const socketId = useConnection((s) => s.socketId);

  useEffect(() => {
    if (!id || !socketId) return;
    const socket = getActiveSocket();
    if (!socket) return;

    const sendForCurrentVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        socket.emit('presence:set', { kind, id });
      } else {
        socket.emit('presence:clear');
      }
    };

    sendForCurrentVisibility();
    document.addEventListener('visibilitychange', sendForCurrentVisibility);

    return () => {
      document.removeEventListener('visibilitychange', sendForCurrentVisibility);
      // Only emit clear if the socket is still up. If it dropped
      // (socketId went to null), the BE already cleared us via the
      // disconnect handler — and emitting on a dead socket would
      // buffer the clear until next connect, leaking it at the wrong time.
      const s = getActiveSocket();
      if (s?.connected) s.emit('presence:clear');
    };
  }, [kind, id, socketId]);
}
